/**
 * TransliterateExtension — TipTap extension that hooks the editor's
 * text-input flow and converts the last Latin-script word into the
 * user's chosen Indic script when they press Space / Enter / a
 * sentence-terminator.
 *
 * Behaviour mirrors <TransliterateInput /> but operates on a TipTap
 * ProseMirror document instead of a plain <textarea>.
 *
 *   • Space  → convert the just-typed word
 *   • Enter  → convert the last word on the line before the break
 *   • . , ; : ! ? → convert the word that preceded the punctuation
 *   • ⌘/Ctrl-Z still undoes the conversion as one step
 *
 * Configuration:
 *   Transliterate.configure({
 *     getLang: () => 'ml',
 *     transliterate: async (word, lang) => 'ഹലോ',
 *   })
 *
 * The extension deliberately does not import an HTTP client so the
 * consuming editor can inject its own (respecting REACT_APP_BACKEND_URL
 * and auth headers).
 */
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

const TRIGGER_KEYS = new Set([" ", "Enter", ".", ",", ";", ":", "!", "?"]);

export const TransliteratePluginKey = new PluginKey("transliterate-live");

export const TransliterateExtension = Extension.create({
  name: "transliterateLive",

  addOptions() {
    return {
      getLang: () => "off",
      transliterate: async (word, _lang) => word,
      // Called with true/false so the toolbar can render a spinner.
      onBusy: (_busy) => {},
    };
  },

  addProseMirrorPlugins() {
    const options = this.options;
    return [
      new Plugin({
        key: TransliteratePluginKey,
        props: {
          handleKeyDown(view, event) {
            const lang = options.getLang();
            if (!lang || lang === "off") return false;
            if (!TRIGGER_KEYS.has(event.key)) return false;
            // Let the key insert first, then swap the word we just terminated.
            // Snapshot the trigger key so multiple queued events don't step on each other.
            setTimeout(() => {
              convertLastWord(view, options).catch(() => {});
            }, 0);
            return false;
          },
        },
      }),
    ];
  },
});

async function convertLastWord(view, options) {
  const lang = options.getLang();
  if (!lang || lang === "off") return;
  const { state } = view;
  const { $from } = state.selection;
  if (!$from.parent.isTextblock) return;
  const blockStart = $from.start();
  const caret = $from.pos;
  const textBefore = state.doc.textBetween(blockStart, caret, "\n", " ");
  if (!textBefore) return;

  // The trigger key is already in the doc. Look for a word terminated by
  // whitespace or a sentence-punctuation character right before the caret.
  const match = textBefore.match(/(\S+?)([\s.,;:!?]+)$/);
  if (!match) return;
  const word = match[1];
  const trailing = match[2].length;
  // Skip words already in a non-Latin script — the user typed them directly.
  if (!word || word.length < 2) return;
  if (/[^\x00-\x7F]/.test(word)) return;
  // Skip URLs, mentions, hashtags, emails — never transliterate those.
  if (/^(https?:\/\/|www\.|[@#])/i.test(word)) return;
  if (word.includes("@") || word.includes("/") || word.includes("://")) return;

  const wordEnd = caret - trailing;
  const wordStart = wordEnd - word.length;
  if (wordStart < blockStart) return;

  options.onBusy?.(true);
  let converted;
  try {
    converted = await options.transliterate(word, lang);
  } finally {
    options.onBusy?.(false);
  }
  if (!converted || converted === word) return;

  // The doc may have moved on while we were awaiting — verify the word
  // we're about to replace still matches exactly.
  const latest = view.state.doc.textBetween(wordStart, wordEnd, "\n", " ");
  if (latest !== word) return;

  const tr = view.state.tr.insertText(converted, wordStart, wordEnd);
  tr.setMeta("addToHistory", true);
  view.dispatch(tr);
}

export default TransliterateExtension;
